import { useEffect, useState } from 'react'
import { Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom'
import { ApiError } from '../api/client.js'
import { getContent } from '../api/endpoints.js'
import { useI18n } from '../i18n/context.js'
import { paths } from '../routes.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { Banner, EmptyState, LinkButton, PageSkeleton } from '../ui/primitives.js'

/**
 * Every `/s/:siteId/…` page passes through here: while the sites load it
 * shows a skeleton, and a site the person cannot see (a wrong link, a site
 * of another client) gets an explanation instead of an empty screen.
 */
export function SiteGate() {
  const { t } = useI18n()
  const { siteId } = useParams<{ siteId: string }>()
  const { sites, loading, error } = useSiteWorkspace()
  if (loading && !sites.length) return <PageSkeleton label={t('common.loading')}/>
  if (error && !sites.length) return <Banner tone="error" title={t('dashboard.error')}>{error}</Banner>
  if (!sites.some((site) => site.id === siteId)) {
    return <EmptyState icon="domain" title={t('site.notFound')} action={<LinkButton variant="filled" icon="home" to={paths.home()}>{t('switcher.allSites')}</LinkButton>}>
      <p>{t('site.notFoundHelp')}</p>
    </EmptyState>
  }
  return <Outlet/>
}

/** `/contenuti/:id`: an old link or a search result without a site; find it, then go to the real address. */
export function ContentResolver() {
  const { t } = useI18n()
  const { id = '', view } = useParams<{ id: string; view?: string }>()
  const [searchParams] = useSearchParams()
  const [target, setTarget] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getContent(id)
      .then((content) => {
        if (cancelled) return
        const upTo = searchParams.get('upTo')
        if (view === 'pagina') setTarget(paths.contentPage(content.siteId, content.id))
        else if (view === 'storia') setTarget(paths.contentHistory(content.siteId, content.id))
        else setTarget(paths.content(content.siteId, content.id, { upTo: upTo && /^\d+$/.test(upTo) ? Number(upTo) : undefined }))
      })
      .catch((err: unknown) => { if (!cancelled) setError(err instanceof ApiError ? err.message : t('edit.loadError')) })
    return () => { cancelled = true }
  }, [id, view, searchParams, t])

  if (target) return <Navigate to={target} replace/>
  if (error) return <Banner tone="error" title={t('edit.loadError')} action={<LinkButton variant="text" to={paths.home()}>{t('switcher.allSites')}</LinkButton>}>{error}</Banner>
  return <PageSkeleton label={t('common.loading')}/>
}

export function NotFoundScreen() {
  const { t } = useI18n()
  return <EmptyState icon="search" title={t('notFound.title')} action={<LinkButton variant="filled" icon="home" to={paths.home()}>{t('switcher.allSites')}</LinkButton>}>
    <p>{t('notFound.help')}</p>
  </EmptyState>
}
